import React, { useEffect, useState } from "react";
import { Card, Dropdown, Button } from "react-bootstrap";
import { useAppContext } from "providers/AppProvider";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import {
  faEye,
  faPen,
  faSyncAlt,
  faBan,
  faCommentDots,
  faPrint,
  faPaperPlane,
  faDownload,
  faHistory,
  faSatelliteDish,
  faComments,
  faCheck,
  faRecycle,
  faBox,
  faBoxOpen,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  ArrowRepeat,
  Chat,
  Shield,
  Gear,
  Building,
  Briefcase,
  Eye,
  ChatFill,
  ExclamationCircle,
} from "react-bootstrap-icons";
import { rgbaColor } from "helpers/utils";

import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import FalconPagination from "components/common/custom/pagination";
import SubtleBadge from "components/common/SubtleBadge";
import CardDropdown from "components/common/CardDropdown";

import PrintComponent from "components/common/custom/print/PrintComponents";
import PreauthorizationPrintUCAF from "./PreauthorizationPrintUCAF";
import PreauthorizationPrintDCAF from "./PreauthorizationPrintDCAF";
import PreauthorizationPrintOCAF from "./PreauthorizationPrintOCAF";
import CancelModal from "./CancelModal";
import ReuseModal from "./ReuseModal";
import HistoryLogModal from "./HistoryLogModal";
import SendCommunicationRequestModal from "./SendCommunicationRequestModal";

import {
  ViewPreauthorization,
  GetPreauthorizationRequest,
  GetPreauthorizationResponse,
  GetPreauthorizationEligibility,
  DownloadRequestJSON,
  DownloadResponseJSON,
  CheckPreauthorizationStatus,
  SubmitSecondResponse,
  GetHistoryLogs,
  GetAllCommunicationHistory,
  GetPreauthorizationCancelResponse,
  GetMissingTooth,
} from "../config/preauthorizationApi";

import paths from "routes/paths";
import WizardModal from "components/wizard/WizardModal";
import SuccessModal from "components/wizard/StatusCheckStatusModal";
import {
  handleResetAll,
  handleResetHistoryLog,
  resetPreauthCancel,
} from "../config/preauthorizationSlice";
import ErrorDisplayModal from "./ErrorDisplayModal";
import CommunicationsListModal from "./CommunicationsListModal";
import PreauthorizationIssueModal from "./PreauthorizationIssueModal";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { toast } from "react-toastify";

const PreauthorizationListTable = ({
  handlePerRowsChange,
  handlePageChange,
  totalRows,
  data,
  page,
  rowsPerPage,
}) => {
  const { missingTeethListReason } = useSelector(
    (state) => state.preauthorization,
  );
  const { getThemeColor } = useAppContext();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { allPermissionType } = useSelector((state) => state.role);
  const preauthorizationSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) => item?.rolemngmttyp_name === "Preauthorization",
  );

  // --- FRONTEND SORTING STATE ---
  const [sorting, setSorting] = useState([]);

  useEffect(() => {
    if (missingTeethListReason?.length === 0) {
      dispatch(GetMissingTooth());
    }
  }, [missingTeethListReason]);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const claimType = params.get("claim_type");

  const [printData, setPrintData] = useState(null);
  const [modalRow, setModalRow] = useState(null);
  const [showCommunicationHistory, setShowCommunicationHistory] =
    useState(false);
  const [showUCAF, setShowUCAF] = useState(false);
  const [showDCAF, setShowDCAF] = useState(false);
  const [showOCAF, setShowOCAF] = useState(false);
  const [successDescription, setSuccessDescription] = useState(false);
  const [showErrorStatusModal, setShowErrorStatusModal] = useState(false);
  const [errorDescription, setErrorDescription] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReuseModal, setShowReuseModal] = useState(false);
  const [showHistoryLogModal, setShowHistoryLogModal] = useState(false);
  const [showSendCommunicationModal, setShowSendCommunicationModal] =
    useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [selectedPreauth, setSelectedPreauth] = useState(null);

  const handlePrintUCAF = async (id, type, row) => {
    setModalRow(row);
    const response = await dispatch(
      ViewPreauthorization({ preauthid: id, claim_type: row?.claim_type }),
    ).unwrap();

    if (response?.success) {
      setPrintData(response?.data);
      if (type === "UCAF") {
        setShowUCAF(true);
      }
      if (type === "DCAF") {
        setShowDCAF(true);
      }
      if (type === "OCAF") {
        setShowOCAF(true);
      }
    }
  };

  const handleFollowup = async (id, row) => {
    const response = await dispatch(
      ViewPreauthorization({
        preauthid: id,
        claim_type: row?.claim_type,
      }),
    ).unwrap();

    if (response?.success) {
      navigate(`${paths.preauthorization}?preauthid=${id}&followup=true`);
    }
  };
  const handleCreateClaim = async (id, row) => {
    const response = await dispatch(
      ViewPreauthorization({
        preauthid: id,
        claim_type: row?.claim_type,
      }),
    ).unwrap();

    if (response?.success) {
      navigate(`${paths.createClaim}?preauthid=${id}&preauth=true`);
    }
  };

  const handleDownloadRequestJson = async (id) => {
    try {
      const response = await dispatch(DownloadRequestJSON(id)).unwrap();
      const downloadUrl = response?.data?.request_file;

      if (!downloadUrl) {
        throw new Error("Download URL not found.");
      }

      const fileResponse = await fetch(downloadUrl);

      if (!fileResponse.ok) {
        throw new Error("Failed to fetch the JSON file.");
      }

      const blob = await fileResponse.blob();
      const urlParts = downloadUrl.split("/");
      const filename = urlParts[urlParts.length - 1] || "downloaded.json";
      saveAs(blob, filename);
    } catch (error) {
      console.error("Error downloading the JSON file:", error);
    }
  };

  const handleCommunicationList = async (row) => {
    const response = await dispatch(
      GetAllCommunicationHistory({
        preauthid: row.preauthfull_id,
        claim_type: row?.claim_type,
      }),
    ).unwrap();

    if (response?.success) {
      setShowCommunicationHistory(true);
      setModalRow(row);
    }
  };

  const handleDownloadResponseJson = async (id, row) => {
    try {
      const response = await dispatch(DownloadResponseJSON(id)).unwrap();
      const downloadUrl = response?.data?.response_file;

      if (!downloadUrl) {
        throw new Error("Download URL not found.");
      }

      const fileResponse = await fetch(downloadUrl);

      if (!fileResponse.ok) {
        throw new Error("Failed to fetch the JSON file.");
      }

      const blob = await fileResponse.blob();
      const urlParts = downloadUrl.split("/");
      const filename = urlParts[urlParts.length - 1] || "downloaded.json";
      saveAs(blob, filename);
    } catch (error) {
      console.error("Error downloading the JSON file:", error);
    }
  };

  const handleEdit = async (id, row) => {
    const response = await dispatch(
      ViewPreauthorization({
        preauthid: id,
        claim_type: row?.claim_type,
      }),
    ).unwrap();

    if (response?.success) {
      navigate(`${paths.preauthorization}?preauthid=${id}&edit=true`);
    }
  };

  const handleViewPreauthIssues = async (row) => {
    const response = await dispatch(
      ViewPreauthorization({
        preauthid:
          row?.preauthinspro_id ||
          row?.preauthpharmcy_id ||
          row?.preauthvision_id ||
          row?.preauthfull_id,
        claim_type: row?.claim_type,
      }),
    ).unwrap();
    if (response?.success) {
      setSelectedPreauth(row);
      setShowIssueModal(true);
    }
  };

  const columns = (setting) => [
    {
      // The ID here doesn't matter because sorting is disabled
      id: "row_number",
      header: "#",
      enableSorting: false, // <--- PREVENTS SORTING SO NUMBERS ALWAYS STAY 1,2,3
      cell: ({ row, table }) => (
        <div className="d-flex align-items-center w-100">
          <div
            className="rounded-circle bg-200 d-flex flex-center fs-11"
            style={{ width: "30px", height: "30px" }}
          >
            {/* Uses the table's internal pagination index so it ALWAYS starts from 1 on every page */}
            {table.getState().pagination.pageIndex *
              table.getState().pagination.pageSize +
              row.index +
              1}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "insurance_shname",
      header: "Payer",
      cell: ({ row: { original } }) => (
        <div className="fs-11 w-100">
          {original.insurance_direct === 0
            ? `${original.payer_name} - ${original.insurance_shname}`
            : original.payer_name}
        </div>
      ),
    },
    {
      accessorKey: "preauthid",
      header: "Preauth ID",
      cell: ({ row: { original } }) => {
        return (
          <div className="d-flex flex-column justify-content-start w-100">
            <div className="text-success fw-medium fs-11">
              {original?.preauthfull_id}
            </div>
            {original?.pauthprior_id && (
              <div className="text-500 fw-normal fs-11">
                {original?.pauthprior_id ? `(${original?.pauthprior_id})` : ""}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "created_at",
      header: "Request Date",
      enableSorting: true,
      accessorFn: (row) =>
        row.is_advpreauth
          ? row.responsedatetime
          : row.updated_at || row.created_at,
      cell: ({ row: { original } }) => (
        <div className="fs-11 w-100">
          {moment(
            original?.is_advpreauth
              ? original?.responsedatetime
              : original.updated_at || original.created_at,
          ).format("DD-MM-YYYY , hh:mm:ss A") || "-"}
        </div>
      ),
    },
    {
      id: "response_date",
      header: "Response Date",
      enableSorting: true,
      accessorFn: (row) => row.responsedatetime || row.updated_at,
      cell: ({ row: { original } }) => (
        <div className="fs-11 w-100">
          {original.responsedatetime
            ? moment(original?.updated_at || original.responsedatetime).format(
                "DD-MM-YYYY , hh:mm:ss A",
              )
            : "-" || "-"}
        </div>
      ),
    },
    {
      accessorKey: "preauthref",
      header: "Preauth Ref No",
      cell: ({ row: { original } }) => (
        <div className="d-flex align-items-center gap-2 fs-11 w-100">
          <div
            className="text-truncate"
            style={{ whiteSpace: "normal", overflowWrap: "break-word" }}
          >
            {original.preauthref || "-"}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "patient",
      header: "Patient",
      cell: ({ row: { original } }) => (
        <div className="d-flex flex-column w-100">
          <div className="fs-11 text-700">
            {`${original.patient_firstname || ""} ${
              original.patient_familyname || ""
            }`}
          </div>
          <div className="text-500 fs-11">
            {original?.patient_identifier
              ? `${original?.patient_identifier || ""} - `
              : ""}{" "}
            {original.patient_nationalid || ""}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "preauthclmtyp",
      header: "PreAuth Type",
      cell: ({ row: { original } }) => {
        const typeConfig = {
          institutional: {
            bg: rgbaColor(getThemeColor("primary"), 0.1),
            color: getThemeColor("primary"),
            icon: <Building />,
          },
          dental: {
            bg: rgbaColor(getThemeColor("danger"), 0.1),
            color: getThemeColor("danger"),
            icon: <i className="fas fa-tooth" />,
          },
          pharmacy: {
            bg: rgbaColor(getThemeColor("info"), 0.1),
            color: getThemeColor("info"),
            icon: <i className="fas fa-pills" />,
          },
          professional: {
            bg: rgbaColor(getThemeColor("success"), 0.1),
            color: getThemeColor("success"),
            icon: <Briefcase />,
          },
          vision: {
            bg: rgbaColor(getThemeColor("warning"), 0.1),
            color: getThemeColor("warning"),
            icon: <Eye />,
          },
        };

        const typeKey = original?.preauthclmtyp?.toLowerCase();
        const config = typeConfig[typeKey] || {
          bg: "#f8f9fa",
          color: "#000",
          icon: <i className="fas fa-question-circle me-1" />,
        };

        return (
          <div className="d-flex flex-column justify-content-start w-100">
            <div className="text-success fw-medium fs-11">
              {original?.preauthclmtyp === "oral"
                ? "Dental"
                : original.preauthclmtyp.charAt(0).toUpperCase() +
                  original.preauthclmtyp.slice(1)}
            </div>
            <div className="text-500 fw-normal fs-11">
              {original?.claimsub_types_disp_name === "oral"
                ? "Dental"
                : original.claimsub_types_disp_name.charAt(0).toUpperCase() +
                  original.claimsub_types_disp_name.slice(1)}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "patient_mrn",
      header: "MRN",
      cell: ({ row: { original } }) => (
        <div className="fs-11 w-100">{original.patient_mrn || "-"}</div>
      ),
    },
    {
      accessorKey: "pre_auth_status",
      header: "PreAuth Status",
      cell: ({ row: { original } }) => {
        const value = original.pre_auth_status?.toLowerCase();
        const statusConfig = {
          error: { bg: "#fde6d8", color: "#ac5a2b", icon: "exclamation" },
          Error: { bg: "#fde6d8", color: "#ac5a2b", icon: "exclamation" },
          invalid: { bg: "#fde6d8", color: "#ac5a2b", icon: "exclamation" },
          "no response": {
            bg: "#08101b",
            color: "#91979e",
            icon: "circle-exclamation",
          },
          approved: { bg: "#d9f8eb", color: "#00894f", icon: "check" },
          valid: { bg: "#e6e8ec", color: "#5d6776", icon: "check" },
          rejected: { bg: "#fbdbe1", color: "#b82c46", icon: "times" },
          cancelled: { bg: "#ffe0fe", color: "#da23aa", icon: "times" },
          partial: {
            bg: "#C2EEF6",
            color: "#0da9bd",
            icon: "star-half-stroke",
          },
          "partially approved": {
            bg: "#C2EEF6",
            color: "#0da9bd",
            icon: "star-half-stroke",
          },
          pended: { bg: "#d9e7fa", color: "#215cac", icon: "clock" },
          "not-required": { bg: "#d8f3ff", color: "#197aa4", icon: "clock" },
          queued: { bg: "#fefad2", color: "#a7970d", icon: "list" },
          adjudication: { bg: "#e8dff7", color: "#6f42c1", icon: "gavel" },
          prepared: { bg: "#e6e8ec", color: "#5d6776", icon: "hammer" },
        };

        const config = statusConfig[value] || {
          bg: "#f8f9fa",
          color: "#000",
          icon: "question",
        };

        return (
          <div className="d-flex justify-content-start w-100">
            <SubtleBadge
              pill
              style={{
                backgroundColor: config.bg,
                color: config.color,
                border: `1px solid ${config.color}20`,
                width: "120px",
              }}
              className="d-flex flex-center fs-12 p-2 gap-1"
            >
              <FontAwesomeIcon
                icon={config.icon}
                transform="shrink-2"
                className="ms-1 me-1"
              />
              {value ? value.charAt(0).toUpperCase() + value.slice(1) : "-"}
            </SubtleBadge>
          </div>
        );
      },
    },
    {
      accessorKey: "employees_firstname",
      header: "Requested By",
      cell: ({ row: { original } }) => (
        <div className="d-flex gap-3 align-items-center w-100">
          <div className="d-flex gap-1 align-items-center">
            {(original.pre_auth_status?.toLowerCase() === "rejected" ||
              original.pre_auth_status?.toLowerCase() === "invalid" ||
              original?.valdn_err) && (
              <div
                className="bg-danger text-white rounded-circle d-flex align-items-center justify-content-center cursor-pointer"
                style={{ width: "20px", height: "20px" }}
                onClick={() => handleViewPreauthIssues(original)}
              >
                <ExclamationCircle size={12} />
              </div>
            )}

            {original.is_secondresponse &&
              !original?.is_secondresponse_view && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip
                      id="tooltip-second-response"
                      style={{
                        "--bs-tooltip-bg": "var(--bs-success)",
                        "--bs-tooltip-color": "var(--bs-white)",
                      }}
                      className="rounded"
                    >
                      Second Response
                    </Tooltip>
                  }
                >
                  <div className="text-success">
                    <ArrowRepeat style={{ width: "16px", height: "16px" }} />
                  </div>
                </OverlayTrigger>
              )}

            {original.is_communication && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip
                    id="tooltip-communication"
                    style={{
                      "--bs-tooltip-bg": original?.is_communication_view
                        ? "var(--bs-warning)"
                        : "var(--bs-warning)",
                      "--bs-tooltip-color": "var(--bs-white)",
                    }}
                    className="rounded"
                  >
                    {original.is_communication_view ? "Read" : "Unread"}
                  </Tooltip>
                }
              >
                <div
                  className={`cursor-pointer ${
                    original.is_communication_view
                      ? "text-warning"
                      : "text-warning"
                  }`}
                  onClick={() => handleCommunicationList(original)}
                >
                  {original.is_communication_view ? (
                    <Chat style={{ width: "16px", height: "16px" }} />
                  ) : (
                    <ChatFill style={{ width: "16px", height: "16px" }} />
                  )}
                </div>
              </OverlayTrigger>
            )}

            {original.is_advpreauth && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip
                    id="tooltip-advanced-preauth"
                    style={{
                      "--bs-tooltip-bg": "var(--bs-info)",
                      "--bs-tooltip-color": "var(--bs-white)",
                    }}
                    className="rounded"
                  >
                    Advanced Authorization
                  </Tooltip>
                }
              >
                <div className="text-info">
                  <Shield style={{ width: "16px", height: "16px" }} />
                </div>
              </OverlayTrigger>
            )}
          </div>

          <div className="fs-11">
            {original.is_advpreauth
              ? "Advance Preauth"
              : `${original.employees_firstname || ""} ${
                  original.employees_familyname || ""
                }`}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "actions",
      header: <Gear />,
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const status = original.pre_auth_status?.toLowerCase();
        const isAdvanced = original.is_advpreauth;

        const handleViewPreauthorization = async (row) => {
          dispatch(resetPreauthCancel());

          navigate(
            `/preauthorization/preauthorization-list/view/${
              row?.preauthinspro_id ||
              row?.preauthpharmcy_id ||
              row?.preauthvision_id ||
              row?.preauthfull_id
            }?claim_type=${row?.claim_type}`,
          );
        };

        const handleCheckStatus = async (id, row) => {
          const response = await dispatch(
            CheckPreauthorizationStatus({
              pauthclmid: id,
              claim_type: row?.claim_type,
              status_type: "preauth",
            }),
          ).unwrap();

          if (!response?.success) {
            toast.error(response?.message_en);
          }

          setShowSuccessModal(true);
          setSuccessDescription(response?.data?.status);
        };

        const handleSecondResponse = async (id, row) => {
          const response = await dispatch(
            SubmitSecondResponse({
              preauthid: id,
              preauthnatre: "secndresp",
              claim_type: row?.claim_type,
            }),
          ).unwrap();

          const errorMessages = response?.data?.second_resp_errormsg;

          if (errorMessages && errorMessages.length > 0) {
            const allDisplays = errorMessages
              .map((err) => err.code?.coding?.[0]?.display)
              .filter(Boolean)
              .join(", ");

            setErrorDescription(allDisplays);
            setShowErrorStatusModal(true);
            return;
          }
          if (response?.success) {
            navigate(
              `/preauthorization/preauthorization-list/view/${response?.data?.preauth_id}?claim_type=${response?.data?.claim_type}`,
            );
          }
        };

        const handleSubmitResponse = async (id, row) => {
          const response = await dispatch(
            SubmitSecondResponse({
              preauthid: id,
              preauthnatre: "submit",
              claim_type: row?.claim_type,
            }),
          ).unwrap();
          if (response?.success) {
            navigate(
              `/preauthorization/preauthorization-list/view/${response?.data?.preauth_id}?claim_type=${response?.data?.claim_type}`,
            );
          }
        };

        const handleReuse = (row) => {
          setShowReuseModal(true);
          setModalRow(row);
        };

        const handleCancel = (row) => {
          setShowCancelModal(true);
          setModalRow(row);
        };

        const handleSendCommunication = (row) => {
          setShowSendCommunicationModal(true);
          setModalRow(row);
        };

        const handleCommunicationList = async (row) => {
          const response = await dispatch(
            GetAllCommunicationHistory({
              preauthid: row.preauthfull_id,
              claim_type: row?.claim_type,
            }),
          ).unwrap();

          if (response?.success) {
            setShowCommunicationHistory(true);
            setModalRow(row);
          }
        };

        const handleOpenHistoryLog = async (id, row) => {
          dispatch(handleResetHistoryLog());

          try {
            const historyResponse = await dispatch(
              GetHistoryLogs({ preauthid: id, claim_type: row?.claim_type }),
            ).unwrap();

            if (historyResponse?.success) {
              setShowHistoryLogModal(true);
              setModalRow(row);
            } else {
              toast.error(historyResponse?.message_en);
            }
          } catch (error) {
            console.error(
              "Error loading history log or preauthorization request:",
              error,
            );
          }
        };

        const isPrepared = status?.toLowerCase() === "prepared";
        const isError = status?.toLowerCase() === "invalid";
        const isPended = status === "pended";
        const allowedPrintStatuses = [
          "approved",
          "prepared",
          "partially approved",
          "cancelled",
          "rejected",
          "not-required",
        ];
        const showPrintOptions = allowedPrintStatuses.includes(status);

        const statusConfig = {
          showView: [
            "approved",
            "cancel",
            "not-required",
            "partially approved",
            "rejected",
            "invalid",
            "pended",
            "prepared",
            "queued",
            "cancelled",
          ].includes(status),
          viewLabel: "View Details/Response",
          showReuse: [
            "approved",
            "cancel",
            "not-required",
            "partially approved",
            "no response",
            "invalid",
            "rejected",
            "queued",
            "cancelled",
          ].includes(status),
          showEdit: ["prepared"].includes(status),
          showSubmit: ["no response", "prepared"].includes(status),
          showStatus: [
            "approved",
            "cancel",
            "not-required",
            "partially approved",
            "queued",
            "rejected",
            "pended",
            "queued",
            "cancelled",
          ].includes(status),
          showCancel: [
            "approved",
            "not-required",
            "partially approved",
            "rejected",
            "queued",
          ].includes(status),
          showFollowup: [
            "approved",
            "rejected",
            "not-required",
            "invalid",
            "partially approved",
            "cancelled",
            "queued",
          ].includes(status),
          showClaim: [
            "approved",
            "partially approved",
            "invalid",
            "not-required",
          ].includes(status),
          showNewCommunication: !isError && !isPrepared,
          showCommunications: !isError && !isPrepared,
          showHistory: !isPrepared,
          showSecondResponse: [
            "pended",
            "no response",
            "approved",
            "queued",
          ].includes(status),
        };

        return (
          <CardDropdown
            icon="ellipsis-v"
            dropdownClassName="py-2 custom-dropdown-menu"
          >
            {isAdvanced ? (
              <>
                {Number(setting?.view) === 1 && statusConfig.showView && (
                  <Dropdown.Item
                    onClick={() => handleViewPreauthorization(original)}
                  >
                    <FontAwesomeIcon icon={faEye} className="me-2" />
                    {statusConfig.viewLabel}
                  </Dropdown.Item>
                )}
                {Number(setting?.submit) === 1 && (
                  <Dropdown.Item
                    onClick={() =>
                      handleCreateClaim(
                        original?.preauthinspro_id ||
                          original?.preauthpharmcy_id ||
                          original?.preauthvision_id ||
                          original?.preauthdental_id ||
                          original?.preauthfull_id,
                        original,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                    Create Claim
                  </Dropdown.Item>
                )}
                {Number(setting?.submit) === 1 && statusConfig.showFollowup && (
                  <Dropdown.Item
                    onClick={() =>
                      handleFollowup(
                        original?.preauthinspro_id ||
                          original?.preauthpharmcy_id ||
                          original?.preauthvision_id ||
                          original?.preauthdental_id ||
                          original?.preauthfull_id,
                        original,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faCommentDots} className="me-2" />
                    Followup
                  </Dropdown.Item>
                )}
                {Number(setting?.cancel) === 1 && statusConfig.showCancel && (
                  <Dropdown.Item onClick={() => handleCancel(original)}>
                    <FontAwesomeIcon icon={faBan} className="me-2" />
                    Cancel
                  </Dropdown.Item>
                )}
                {statusConfig.showStatus && (
                  <Dropdown.Item
                    onClick={() =>
                      handleCheckStatus(original?.preauthid, original)
                    }
                  >
                    <FontAwesomeIcon icon={faSatelliteDish} className="me-2" />
                    Status Check
                  </Dropdown.Item>
                )}
                {(Number(setting?.view) === 1 ||
                  Number(setting?.submit) === 1) &&
                  statusConfig.showCommunications && (
                    <Dropdown.Item
                      onClick={() => handleCommunicationList(original)}
                    >
                      <FontAwesomeIcon icon={faComments} className="me-2" />
                      Communications
                    </Dropdown.Item>
                  )}
                {Number(setting?.submit) === 1 && (
                  <Dropdown.Item
                    onClick={() => handleSendCommunication(original)}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                    New Communication
                  </Dropdown.Item>
                )}
                {Number(setting?.export) === 1 &&
                  !["no response", "prepared"].includes(status) && (
                    <Dropdown.Item
                      onClick={() =>
                        handleDownloadResponseJson({
                          preauthid:
                            original?.preauthinspro_id ||
                            original?.preauthpharmcy_id ||
                            original?.preauthvision_id ||
                            original?.preauthfull_id ||
                            original?.preauth_id,
                          claim_type: original?.claim_type,
                        })
                      }
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                      Download JSON Response
                    </Dropdown.Item>
                  )}
                {Number(setting?.view) === 1 && statusConfig.showHistory && (
                  <Dropdown.Item
                    onClick={() =>
                      handleOpenHistoryLog(
                        original?.preauthinspro_id ||
                          original?.preauthpharmcy_id ||
                          original?.preauthvision_id ||
                          original?.preauthfull_id ||
                          original?.preauth_id,
                        original,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faHistory} className="me-2" />
                    History Log
                  </Dropdown.Item>
                )}
                {Number(setting?.print) === 1 && showPrintOptions && (
                  <>
                    <Dropdown.Item
                      onClick={() =>
                        handlePrintUCAF(
                          original?.preauthinspro_id ||
                            original?.preauthpharmcy_id ||
                            original?.preauthvision_id ||
                            original?.preauthfull_id ||
                            original?.preauth_id,
                          "DCAF",
                          original,
                        )
                      }
                    >
                      <FontAwesomeIcon icon={faPrint} className="me-2" />
                      Print DCAF
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        handlePrintUCAF(
                          original?.preauthinspro_id ||
                            original?.preauthpharmcy_id ||
                            original?.preauthvision_id ||
                            original?.preauthfull_id ||
                            original?.preauth_id,
                          "UCAF",
                          original,
                        )
                      }
                    >
                      <FontAwesomeIcon icon={faPrint} className="me-2" />
                      Print UCAF
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        handlePrintUCAF(
                          original?.preauthinspro_id ||
                            original?.preauthpharmcy_id ||
                            original?.preauthvision_id ||
                            original?.preauthfull_id ||
                            original?.preauth_id,
                          "OCAF",
                          original,
                        )
                      }
                    >
                      <FontAwesomeIcon icon={faPrint} className="me-2" />
                      Print OCAF
                    </Dropdown.Item>
                  </>
                )}
              </>
            ) : (
              <>
                {Number(setting?.view) === 1 && statusConfig.showView && (
                  <Dropdown.Item
                    onClick={() => handleViewPreauthorization(original)}
                  >
                    <FontAwesomeIcon icon={faEye} className="me-2" />
                    {statusConfig.viewLabel}
                  </Dropdown.Item>
                )}

                {Number(setting?.submit) === 1 && statusConfig?.showClaim && (
                  <Dropdown.Item
                    onClick={() =>
                      handleCreateClaim(
                        original?.preauthinspro_id ||
                          original?.preauthpharmcy_id ||
                          original?.preauthvision_id ||
                          original?.preauthdental_id ||
                          original?.preauthfull_id,
                        original,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                    Create Claim
                  </Dropdown.Item>
                )}
                {Number(setting?.edit) === 1 && statusConfig.showEdit && (
                  <Dropdown.Item
                    onClick={() =>
                      handleEdit(
                        original?.preauthinspro_id ||
                          original?.preauthpharmcy_id ||
                          original?.preauthvision_id ||
                          original?.preauthdental_id ||
                          original?.preauthfull_id,
                        original,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faPen} className="me-2" />
                    Edit
                  </Dropdown.Item>
                )}
                {Number(setting?.submit) === 1 && statusConfig.showSubmit && (
                  <Dropdown.Item
                    onClick={() =>
                      handleSubmitResponse(
                        original?.preauthinspro_id ||
                          original?.preauthpharmcy_id ||
                          original?.preauthvision_id ||
                          original?.preauthdental_id ||
                          original?.preauthfull_id,
                        original,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    Submit
                  </Dropdown.Item>
                )}

                {Number(setting?.add) === 1 && statusConfig.showReuse && (
                  <Dropdown.Item onClick={() => handleReuse(original)}>
                    <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
                    Reuse
                  </Dropdown.Item>
                )}

                {statusConfig.showStatus && (
                  <Dropdown.Item
                    onClick={() =>
                      handleCheckStatus(original?.preauthid, original)
                    }
                  >
                    <FontAwesomeIcon icon={faSatelliteDish} className="me-2" />
                    Status Check
                  </Dropdown.Item>
                )}
                {statusConfig.showSecondResponse && (
                  <Dropdown.Item
                    onClick={() =>
                      handleSecondResponse(
                        original?.preauthinspro_id ||
                          original?.preauthpharmcy_id ||
                          original?.preauthvision_id ||
                          original?.preauthdental_id ||
                          original?.preauthfull_id,
                        original,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faRecycle} className="me-2" />
                    Second Response
                  </Dropdown.Item>
                )}

                {Number(setting?.cancel) === 1 && statusConfig.showCancel && (
                  <Dropdown.Item onClick={() => handleCancel(original)}>
                    <FontAwesomeIcon icon={faBan} className="me-2" />
                    Cancel
                  </Dropdown.Item>
                )}

                {Number(setting?.submit) === 1 && statusConfig.showFollowup && (
                  <Dropdown.Item
                    onClick={() =>
                      handleFollowup(
                        original?.preauthinspro_id ||
                          original?.preauthpharmcy_id ||
                          original?.preauthvision_id ||
                          original?.preauthdental_id ||
                          original?.preauthfull_id,
                        original,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faCommentDots} className="me-2" />
                    Followup
                  </Dropdown.Item>
                )}

                {Number(setting?.print) === 1 && showPrintOptions && (
                  <>
                    <Dropdown.Item
                      onClick={() =>
                        handlePrintUCAF(
                          original?.preauthinspro_id ||
                            original?.preauthpharmcy_id ||
                            original?.preauthvision_id ||
                            original?.preauthfull_id ||
                            original?.preauth_id,
                          "DCAF",
                          original,
                        )
                      }
                    >
                      <FontAwesomeIcon icon={faPrint} className="me-2" />
                      Print DCAF
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        handlePrintUCAF(
                          original?.preauthinspro_id ||
                            original?.preauthpharmcy_id ||
                            original?.preauthvision_id ||
                            original?.preauthfull_id ||
                            original?.preauth_id,
                          "UCAF",
                          original,
                        )
                      }
                    >
                      <FontAwesomeIcon icon={faPrint} className="me-2" />
                      Print UCAF
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        handlePrintUCAF(
                          original?.preauthinspro_id ||
                            original?.preauthpharmcy_id ||
                            original?.preauthvision_id ||
                            original?.preauthfull_id ||
                            original?.preauth_id,
                          "OCAF",
                          original,
                        )
                      }
                    >
                      <FontAwesomeIcon icon={faPrint} className="me-2" />
                      Print OCAF
                    </Dropdown.Item>
                  </>
                )}

                {(Number(setting?.view) === 1 ||
                  Number(setting?.submit) === 1) &&
                  statusConfig.showCommunications && (
                    <Dropdown.Item
                      onClick={() => handleCommunicationList(original)}
                    >
                      <FontAwesomeIcon icon={faComments} className="me-2" />
                      Communications
                    </Dropdown.Item>
                  )}

                {Number(setting?.submit) === 1 &&
                  statusConfig.showNewCommunication && (
                    <Dropdown.Item
                      onClick={() => handleSendCommunication(original)}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                      New Communication
                    </Dropdown.Item>
                  )}

                {Number(setting?.export) === 1 &&
                  (["no response", "prepared"].includes(status) ? (
                    <Dropdown.Item
                      onClick={() =>
                        handleDownloadRequestJson({
                          preauthid:
                            original?.preauthinspro_id ||
                            original?.preauthpharmcy_id ||
                            original?.preauthvision_id ||
                            original?.preauthfull_id ||
                            original?.preauth_id,
                          claim_type: original?.claim_type,
                        })
                      }
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                      Download JSON Request
                    </Dropdown.Item>
                  ) : (
                    <>
                      <Dropdown.Item
                        onClick={() =>
                          handleDownloadRequestJson({
                            preauthid:
                              original?.preauthinspro_id ||
                              original?.preauthpharmcy_id ||
                              original?.preauthvision_id ||
                              original?.preauthfull_id ||
                              original?.preauth_id,
                            claim_type: original?.claim_type,
                          })
                        }
                      >
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        Download JSON Request
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() =>
                          handleDownloadResponseJson({
                            preauthid:
                              original?.preauthinspro_id ||
                              original?.preauthpharmcy_id ||
                              original?.preauthvision_id ||
                              original?.preauthfull_id ||
                              original?.preauth_id,
                            claim_type: original?.claim_type,
                          })
                        }
                      >
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        Download JSON Response
                      </Dropdown.Item>
                    </>
                  ))}

                {Number(setting?.view) === 1 && statusConfig.showHistory && (
                  <Dropdown.Item
                    onClick={() =>
                      handleOpenHistoryLog(
                        original?.preauthinspro_id ||
                          original?.preauthpharmcy_id ||
                          original?.preauthvision_id ||
                          original?.preauthfull_id ||
                          original?.preauth_id,
                        original,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faHistory} className="me-2" />
                    History Log
                  </Dropdown.Item>
                )}
              </>
            )}
          </CardDropdown>
        );
      },
    },
  ]; // end columns function

  const table = useAdvanceTable({
    data,
    columns: columns(preauthorizationSetting),
    manualPagination: true,
    pageCount: Math.ceil(totalRows / rowsPerPage),
    state: {
      pagination: {
        pageIndex: page,
        pageSize: rowsPerPage,
      },
      sorting,
    },
    onSortingChange: setSorting,
    manualSorting: false,
  });

  useEffect(() => {
    table.setPagination({ pageIndex: page, pageSize: rowsPerPage });
  }, [page, rowsPerPage]);

  return (
    <div className="mb-3 mt-3 border">
      {Number(preauthorizationSetting?.list) === 1 ? (
        data?.length > 0 ? (
          <>
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                tableProps={{
                  size: "sm",
                  className: "fs-10 mb-0 overflow-hidden",
                  striped: true,
                  hover: true,
                }}
                headerClassName="bg-200 text-nowrap align-middle"
                rowClassName="align-middle white-space-nowrap"
              />
            </AdvanceTableProvider>
            <Card.Footer className="d-flex mt-2 mb-3 justify-content-end align-items-center">
              <FalconPagination
                currentPage={page}
                rowsPerPage={rowsPerPage}
                totalRecords={totalRows}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePerRowsChange}
              />
            </Card.Footer>
          </>
        ) : (
          <div className="text-center py-3">No Data To Show</div>
        )
      ) : (
        <div className="text-center py-5">
          <h5 className="text-danger">Permission Denied</h5>
          <p className="text-muted fs-11">
            You do not have permission to view this list.
          </p>
        </div>
      )}

      {showUCAF && (
        <PrintComponent
          text=""
          onPrintOpen={() => {
            setShowUCAF(false);
          }}
          child={
            <PreauthorizationPrintUCAF printData={printData} row={modalRow} />
          }
        />
      )}

      {showDCAF && (
        <PrintComponent
          text=""
          onPrintOpen={() => setShowDCAF(false)}
          child={
            <PreauthorizationPrintDCAF printData={printData} row={modalRow} />
          }
        />
      )}
      {showOCAF && (
        <PrintComponent
          text=""
          onPrintOpen={() => setShowOCAF(false)}
          child={
            <PreauthorizationPrintOCAF printData={printData} row={modalRow} />
          }
        />
      )}

      <CancelModal
        show={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        row={modalRow}
        page={page}
        rowsPerPage={rowsPerPage}
      />
      <ReuseModal
        show={showReuseModal}
        onClose={() => setShowReuseModal(false)}
        row={modalRow}
        page={page}
        rowsPerPage={rowsPerPage}
      />

      <ErrorDisplayModal
        modal={showErrorStatusModal}
        setModal={setShowErrorStatusModal}
        title="Status Information"
        description={errorDescription}
      />
      <SuccessModal
        modal={showSuccessModal}
        setModal={setShowSuccessModal}
        title="Status Information"
        description={successDescription}
      />

      <HistoryLogModal
        modal={showHistoryLogModal}
        setModal={setShowHistoryLogModal}
        row={modalRow}
      />
      <SendCommunicationRequestModal
        modal={showSendCommunicationModal}
        setModal={setShowSendCommunicationModal}
        modalRow={modalRow}
      />

      <CommunicationsListModal
        show={showCommunicationHistory}
        onHide={() => setShowCommunicationHistory(false)}
        modalRow={modalRow}
      />
      <PreauthorizationIssueModal
        show={showIssueModal}
        handleClose={() => setShowIssueModal(false)}
        preauth={selectedPreauth}
      />
    </div>
  );
};

export default PreauthorizationListTable;
